import React, { useState, useEffect } from 'react';
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBSelect,
  MDBAccordion,
  MDBAccordionItem,
  MDBCheckbox,
  MDBSpinner,
  MDBRow,
  MDBCol
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { generateClient } from 'aws-amplify/api';
import { getLocalizedText } from '../../../utils/localizedText';
import { isoToGermanDate } from '../../../utils/dateTools';
import { captureError } from '../../../utils/sentryHelpers';

// Feld-Konfiguration für kopierbare Felder
const COPYABLE_FIELDS = [
  // Allgemein
  { key: 'name', label: 'Name', category: 'general', type: 'bilingual' },
  { key: 'type', label: 'Event Type', category: 'general', type: 'eventType' },
  { key: 'status', label: 'Status', category: 'general', type: 'status' },
  { key: 'landingPageUrl', label: 'Landing Page URL', category: 'general', type: 'text' },
  { key: 'industries', label: 'Industries', category: 'general', type: 'industries' },

  // Vorträge & Aktionen
  { key: 'exhibitorLectures', label: 'Exhibitor lectures', category: 'lectures', type: 'boolean' },
  { key: 'lectureRooms', label: 'Lecture rooms', category: 'lectures', type: 'number' },

  // Stände
  { key: 'boothBooking', label: 'Booth booking', category: 'booths', type: 'boolean' },

  // Ticket-Registrierung
  { key: 'enableTicketBooking', label: 'Enable ticket booking', category: 'tickets', type: 'boolean' },
  { key: 'hasInvitationCodes', label: 'Invitation codes', category: 'tickets', type: 'boolean' },
  { key: 'bookingSelectDays', label: 'Selectable participant days', category: 'tickets', type: 'boolean' },
  { key: 'suggestedInvitationCode', label: 'Suggested Invitation Code', category: 'tickets', type: 'text' },
  { key: 'ticketPricePerDay', label: 'Ticket price per day', category: 'tickets', type: 'boolean' },
  { key: 'instantPaymentOnly', label: 'Instant payment only (Stripe)', category: 'tickets', type: 'boolean' },
  { key: 'autoGenerateInvoice', label: 'Auto-generate invoice', category: 'tickets', type: 'boolean' },
  { key: 'paymentFlow', label: 'Payment flow', category: 'tickets', type: 'text' },

  // Location - aufgeteilt in einzelne Felder
  { key: 'location.name', label: 'Location Name', category: 'location', type: 'locationName' },
  { key: 'location.address', label: 'Address', category: 'location', type: 'locationAddress' },
  { key: 'location.coordinates', label: 'Coordinates', category: 'location', type: 'locationCoordinates' },
  { key: 'location.travelAndParkingNotice', label: 'Travel and Parking Notice', category: 'location', type: 'bilingual' },

  // Texte
  { key: 'description', label: 'Description', category: 'texts', type: 'bilingual' },
  { key: 'bookingNotice', label: 'Booking Notice', category: 'texts', type: 'bilingual' },
  { key: 'ticketNotice', label: 'Ticket Notice', category: 'texts', type: 'bilingual' },
  { key: 'lecturesNotice', label: 'Lectures Notice', category: 'texts', type: 'bilingual' },
  { key: 'ticketOrganizerLine', label: 'Ticket Organizer Line', category: 'texts', type: 'bilingual' },
  { key: 'bookingTicketBulletPoints', label: 'Booking Bullet Points', category: 'texts', type: 'bulletPoints' },

  // Website-Texte
  { key: 'landingPageIntroText', label: 'Introduction Text', category: 'website', type: 'bilingual' },
  { key: 'landingPageProgramText', label: 'Program Text', category: 'website', type: 'bilingual' },
  { key: 'landingPageProgramSpeakersTitle', label: 'Program Speakers Title', category: 'website', type: 'bilingual' },
  { key: 'landingPageProgramSpeakersText', label: 'Program Speakers Text', category: 'website', type: 'bilingual' },
  { key: 'landingPageBookingText', label: 'Booking Text', category: 'website', type: 'bilingual' },
  { key: 'landingPageBookingPandCText', label: 'Booking Payment and Cancelation Text', category: 'website', type: 'bilingual' },
  { key: 'heroBannerStats', label: 'Hero Banner Stats', category: 'website', type: 'object' },
  { key: 'infoTextExhibitors', label: 'Info Text for Exhibitors', category: 'website', type: 'bilingual' },
  { key: 'infoTextVisitors', label: 'Info Text for Visitors', category: 'website', type: 'bilingual' },
  { key: 'infoTextTravel', label: 'Info Text for Travel & Directions', category: 'website', type: 'bilingual' },
  { key: 'contactPersons', label: 'Contact Persons', category: 'website', type: 'object' },
  { key: 'highlights', label: 'Highlights', category: 'website', type: 'object' },

  // Rechtliches
  { key: 'ticketTAC', label: 'Ticket Terms and Conditions', category: 'legal', type: 'bilingual' },
  { key: 'ticketPrivacyPolicy', label: 'Ticket Privacy Policy', category: 'legal', type: 'bilingual' },
  { key: 'boothTAC', label: 'Booth Terms and Conditions', category: 'legal', type: 'bilingual' },
  { key: 'boothPrivacyPolicy', label: 'Booth Privacy Policy', category: 'legal', type: 'bilingual' },
];

const CATEGORIES = [
  { key: 'general', label: 'General' },
  { key: 'lectures', label: 'Lectures and Actions' },
  { key: 'booths', label: 'Booths' },
  { key: 'tickets', label: 'Ticket registration' },
  { key: 'location', label: 'Location' },
  { key: 'texts', label: 'Texts' },
  { key: 'website', label: 'Website' },
  { key: 'legal', label: 'Terms & Privacy' },
];

// GraphQL Queries
const LIST_EVENTS_QUERY = /* GraphQL */ `
  query ListEventsForCopy {
    listEvents(limit: 100) {
      items {
        id
        name {
          language
          text
        }
        startDate
        endDate
      }
    }
  }
`;

const GET_EVENT_QUERY = /* GraphQL */ `
  query GetEvent($id: ID!) {
    getEvent(id: $id) {
      id
      type
      name {
        language
        text
      }
      description {
        language
        text
      }
      bookingNotice {
        language
        text
      }
      ticketNotice {
        language
        text
      }
      location {
        name {
          language
          text
        }
        travelAndParkingNotice {
          language
          text
        }
        address {
          street
          streetNumber
          zip
          city
          countryCode
        }
        coordinates {
          lat
          lng
        }
      }
      ticketOrganizerLine {
        language
        text
      }
      status
      enableTicketBooking
      hasInvitationCodes
      suggestedInvitationCode
      bookingSelectDays
      ticketPricePerDay
      instantPaymentOnly
      autoGenerateInvoice
      paymentFlow
      boothBooking
      exhibitorLectures
      lectureRooms
      landingPageUrl
      lecturesNotice {
        language
        text
      }
      industries {
        items {
          industry {
            id
            name {
              language
              text
            }
          }
        }
      }
      bookingTicketBulletPoints {
        text {
          language
          text
        }
      }
      ticketTAC {
        language
        text
      }
      ticketPrivacyPolicy {
        language
        text
      }
      boothTAC {
        language
        text
      }
      boothPrivacyPolicy {
        language
        text
      }
      landingPageIntroText {
        language
        text
      }
      landingPageProgramText {
        language
        text
      }
      landingPageProgramSpeakersTitle {
        language
        text
      }
      landingPageProgramSpeakersText {
        language
        text
      }
      landingPageBookingText {
        language
        text
      }
      landingPageBookingPandCText {
        language
        text
      }
      heroBannerStats {
        language
        text
      }
      infoTextExhibitors {
        language
        text
      }
      infoTextVisitors {
        language
        text
      }
      infoTextTravel {
        language
        text
      }
      contactPersons {
        name
        department
        phone
        email
        image {
          fileName
          alt
        }
      }
      highlights {
        mainVideo {
          fileName
          s3Path
        }
        mainText {
          language
          text
        }
        dailys {
          date
          videos {
            fileName
            s3Path
          }
          text {
            language
            text
          }
        }
        images {
          image {
            fileName
            alt
          }
          tags
        }
        videos {
          file {
            fileName
            s3Path
          }
          tags
        }
        pressMedia {
          text {
            language
            text
          }
          media {
            fileName
            s3Path
          }
        }
        exhibitorInfo {
          pricingText {
            language
            text
          }
        }
      }
    }
  }
`;

// Helper Funktionen für Vorschau
const truncate = (text, maxLength = 80) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

// Helper um nested values zu holen (z.B. "location.name")
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const getFieldPreview = (field, event, industries, t) => {
  let value;

  if (field.key === 'industries') {
    value = industries;
  } else if (field.key.includes('.')) {
    value = getNestedValue(event, field.key);
  } else {
    value = event[field.key];
  }

  if (value === undefined || value === null) return null;

  switch (field.type) {
    case 'bilingual':
      const text = getLocalizedText(value);
      return truncate(stripHtml(text));

    case 'boolean':
      return value ? t('Yes') : t('No');

    case 'number':
      return value?.toString() || '';

    case 'text':
      return truncate(value);

    case 'eventType':
      return value === 'fair' ? t('Fair') : value === 'seminar' ? t('Seminar') : value;

    case 'status':
      return value === 'active' ? t('Active') : t('Disabled');

    case 'locationName':
      return truncate(getLocalizedText(value));

    case 'locationAddress':
      if (!value) return null;
      return truncate(`${value.street || ''} ${value.streetNumber || ''}, ${value.zip || ''} ${value.city || ''}`);

    case 'locationCoordinates':
      if (!value || (!value.lat && !value.lng)) return null;
      return `${value.lat}, ${value.lng}`;

    case 'industries':
      if (!value || value.length === 0) return null;
      const industryNames = value.map(ind => getLocalizedText(ind.name)).join(', ');
      return truncate(industryNames);

    case 'bulletPoints':
      if (!value || value.length === 0) return null;
      return `${value.length} ${t('Bullet Points')}`;

    case 'object':
      return t('Available');

    default:
      return truncate(String(value));
  }
};

const hasFieldValue = (field, event, industries) => {
  let value;

  if (field.key === 'industries') {
    value = industries;
  } else if (field.key.includes('.')) {
    value = getNestedValue(event, field.key);
  } else {
    value = event[field.key];
  }

  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return true;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    // Für bilingual arrays prüfen ob mindestens ein Text vorhanden ist
    if (value[0]?.language !== undefined) {
      return value.some(item => item.text && item.text.length > 0);
    }
    return true;
  }
  if (typeof value === 'object') {
    // Für locationAddress prüfen
    if (field.type === 'locationAddress') {
      return value.street?.length > 0 || value.city?.length > 0;
    }
    // Für locationCoordinates prüfen
    if (field.type === 'locationCoordinates') {
      return value.lat !== undefined || value.lng !== undefined;
    }
    return Object.keys(value).length > 0;
  }
  return false;
};

const CopyFromEventModal = ({ open, onClose, onApply, allIndustries }) => {
  const { t, i18n } = useTranslation();
  const client = generateClient();

  const [events, setEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [sourceEvent, setSourceEvent] = useState(null);
  const [sourceIndustries, setSourceIndustries] = useState([]);
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);

  // Lade Event-Liste beim Öffnen
  useEffect(() => {
    if (open) {
      fetchEvents();
    }
  }, [open]);

  // Lade Event-Details wenn ein Event ausgewählt wird
  useEffect(() => {
    if (selectedEventId) {
      fetchEventDetails(selectedEventId);
    } else {
      setSourceEvent(null);
      setSourceIndustries([]);
      setSelectedFields([]);
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    setIsLoadingEvents(true);
    try {
      const response = await client.graphql({
        query: LIST_EVENTS_QUERY
      });
      const sortedEvents = response.data.listEvents.items.sort(
        (a, b) => new Date(b.startDate) - new Date(a.startDate)
      );
      setEvents(sortedEvents);
    } catch (error) {
      captureError(error, {
        feature: 'event-management',
        operation: 'fetch-events-for-copy',
        context: {}
      });
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const fetchEventDetails = async (eventId) => {
    setIsLoadingEvent(true);
    try {
      const response = await client.graphql({
        query: GET_EVENT_QUERY,
        variables: { id: eventId }
      });
      const event = response.data.getEvent;
      setSourceEvent(event);

      // Extrahiere Industries mit vollständigen Daten
      const industryIds = event.industries?.items?.map(item => item.industry.id) || [];
      const matchedIndustries = allIndustries?.filter(ind => industryIds.includes(ind.id)) || [];
      setSourceIndustries(matchedIndustries);

    } catch (error) {
      captureError(error, {
        feature: 'event-management',
        operation: 'fetch-event-details-for-copy',
        context: { eventId }
      });
    } finally {
      setIsLoadingEvent(false);
    }
  };

  const handleFieldToggle = (fieldKey) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldKey)) {
        return prev.filter(f => f !== fieldKey);
      } else {
        return [...prev, fieldKey];
      }
    });
  };

  const handleSelectAllInCategory = (category, fieldsInCategory) => {
    const availableFields = fieldsInCategory.filter(f => hasFieldValue(f, sourceEvent, sourceIndustries));
    const allSelected = availableFields.every(f => selectedFields.includes(f.key));

    if (allSelected) {
      // Deselect all in category
      setSelectedFields(prev => prev.filter(key => !availableFields.some(f => f.key === key)));
    } else {
      // Select all in category
      const newFields = availableFields.map(f => f.key).filter(key => !selectedFields.includes(key));
      setSelectedFields(prev => [...prev, ...newFields]);
    }
  };

  const handleApply = () => {
    const sourceIndustryIds = sourceIndustries.map(ind => ind.id);
    onApply(selectedFields, sourceEvent, sourceIndustryIds);
  };

  const handleClose = () => {
    setSelectedEventId(null);
    setSourceEvent(null);
    setSourceIndustries([]);
    setSelectedFields([]);
    onClose();
  };

  // Erstelle Event-Optionen für Select
  const eventOptions = [
    { text: t('Select an event'), value: '' },
    ...events.map(event => ({
      text: `${getLocalizedText(event.name)} (${event.startDate ? isoToGermanDate(event.startDate) : t('No date')})`,
      value: event.id
    }))
  ];

  // Gruppiere Felder nach Kategorie und filtere leere
  const getFieldsForCategory = (categoryKey) => {
    return COPYABLE_FIELDS.filter(f => f.category === categoryKey);
  };

  const getCategoryHasFields = (categoryKey) => {
    if (!sourceEvent) return false;
    const fields = getFieldsForCategory(categoryKey);
    return fields.some(f => hasFieldValue(f, sourceEvent, sourceIndustries));
  };

  return (
    <MDBModal open={open} onClose={handleClose} tabIndex='-1'>
      <MDBModalDialog size="lg" scrollable>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>{t('Copy data from event')}</MDBModalTitle>
            <MDBBtn className='btn-close' color='none' onClick={handleClose}></MDBBtn>
          </MDBModalHeader>

          <MDBModalBody>
            {isLoadingEvents ? (
              <div className="text-center py-4">
                <MDBSpinner />
              </div>
            ) : (
              <>
                <MDBSelect
                  label={t('Select source event')}
                  data={eventOptions}
                  value={selectedEventId || ''}
                  onChange={(e) => setSelectedEventId(e.value || null)}
                  search
                  searchLabel={t('Search')}
                  className="mb-4"
                />

                {isLoadingEvent && (
                  <div className="text-center py-4">
                    <MDBSpinner />
                  </div>
                )}

                {sourceEvent && !isLoadingEvent && (
                  <MDBAccordion alwaysOpen>
                    {CATEGORIES.map((category, index) => {
                      if (!getCategoryHasFields(category.key)) return null;

                      const fieldsInCategory = getFieldsForCategory(category.key);
                      const availableFields = fieldsInCategory.filter(f => hasFieldValue(f, sourceEvent, sourceIndustries));
                      const allSelected = availableFields.every(f => selectedFields.includes(f.key));

                      return (
                        <MDBAccordionItem
                          key={category.key}
                          collapseId={index + 1}
                          headerTitle={
                            <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                              <span>{t(category.label)}</span>
                              <span className="badge bg-secondary ms-2">
                                {availableFields.filter(f => selectedFields.includes(f.key)).length}/{availableFields.length}
                              </span>
                            </div>
                          }
                        >
                          <div className="mb-2">
                            <MDBBtn
                              size="sm"
                              color="link"
                              onClick={() => handleSelectAllInCategory(category.key, fieldsInCategory)}
                            >
                              {allSelected ? t('Deselect all') : t('Select all')}
                            </MDBBtn>
                          </div>

                          {availableFields.map(field => {
                            const preview = getFieldPreview(field, sourceEvent, sourceIndustries, t);

                            return (
                              <MDBRow key={field.key} className="py-2 border-bottom align-items-center">
                                <MDBCol size="1">
                                  <MDBCheckbox
                                    checked={selectedFields.includes(field.key)}
                                    onChange={() => handleFieldToggle(field.key)}
                                    id={`field-${field.key}`}
                                  />
                                </MDBCol>
                                <MDBCol size="4">
                                  <label
                                    htmlFor={`field-${field.key}`}
                                    className="mb-0 fw-bold"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {t(field.label)}
                                  </label>
                                </MDBCol>
                                <MDBCol size="7">
                                  <small className="text-muted">{preview}</small>
                                </MDBCol>
                              </MDBRow>
                            );
                          })}
                        </MDBAccordionItem>
                      );
                    })}
                  </MDBAccordion>
                )}
              </>
            )}
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn color="secondary" onClick={handleClose}>
              {t('Cancel')}
            </MDBBtn>
            <MDBBtn
              color="primary"
              onClick={handleApply}
              disabled={selectedFields.length === 0}
            >
              {t('Apply')} {selectedFields.length > 0 && `(${selectedFields.length})`}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
};

export default CopyFromEventModal;
